/**
 * "No mail? Upload the customer's list" — reading a bare file the purchaser picked.
 *
 * ---- Why this is client-side, and needs no endpoint ----
 *
 * The button behind this was disabled for a month behind `liveAvailability.ts`'s
 * `enquiryExtractUpload` (`A3`), waiting on a server method for a mail-less file extraction. It
 * does not need one: everything required is already here, because the SAME parsers read mail
 * attachments today — `read-excel-file` for OOXML, `legacyXls` for OLE2 workbooks, `legacyDoc` for
 * OLE2 Word, `unpdf` for PDFs. The server-side alternative is dead anyway (`B1`).
 *
 * So this reads the file locally and hands back lines, exactly as the mail path does. The only
 * thing an endpoint would add is server-side storage of the file, which is a different feature
 * (`mail.upload_attachment`, already wired for composing).
 *
 * ---- The format is decided on the BYTES ----
 *
 * `sniffFileKind` first, never the extension: a `.xls` that is really OOXML, an `.xlsm` the server
 * recorded with no type, a `.doc` renamed by a supplier — all are unambiguous in their header and
 * routinely wrong in their name.
 *
 * `ole2` needs the container opened to tell Word from Excel, so both are tried: Word first (it
 * looks for a `WordDocument` stream and returns nothing if absent), then the workbook reader.
 * `zip` likewise: a .docx (`word/document.xml`) before the OOXML workbook reader. A CSV has no
 * signature at all and is the one case decided on the file name (`docxCsv.ts`).
 */

import { sniffFileKind } from './fileKind'
import { readMailItems, type ReadItemsResult } from './readMailItems'
import type { LegacySheet } from './legacyXls'

/** How many bytes the sniffer needs. */
const HEAD = 8

async function sheetsFrom(file: File, bytes: Uint8Array): Promise<LegacySheet[] | null> {
  const kind = sniffFileKind(bytes.subarray(0, HEAD))

  if (kind === 'ole2') {
    // Word FIRST: `parseLegacyDoc` returns `[]` for a container with no `WordDocument` stream, so
    // an `.xls` falls straight through to the workbook reader below.
    const { parseLegacyDoc } = await import('./legacyDoc')
    const doc = await parseLegacyDoc(bytes, file.name)
    if (doc.length > 0) return doc

    const { parseLegacyXls } = await import('./legacyXls')
    return parseLegacyXls(bytes)
  }

  if (kind === 'zip') {
    // A Word file is a zip too (2026-09-17): its tables read as sheets — see `docxCsv.ts`.
    const { docxSheets, looksLikeDocx } = await import('./docxCsv')
    if (looksLikeDocx(bytes)) return docxSheets(bytes)
    /*
      `/universal`, and `getSheets` — the same two details the mail path had to get right. The bare
      entry point resolves to the Node build under Vite's SSR-ish conditions, and `readSheet`
      silently ignores `getSheets` and returns ROWS; both mistakes read only the first sheet, which
      halves a requisition that runs onto a second one.
    */
    const { default: readWorkbook } = await import('read-excel-file/universal')
    const raw = (await readWorkbook(file, { getSheets: true } as never)) as unknown
    if (!Array.isArray(raw)) return []
    return raw
      .filter(
        (entry): entry is { sheet?: string; data: LegacySheet['rows'] } =>
          typeof entry === 'object' && entry !== null && Array.isArray((entry as { data?: unknown }).data),
      )
      .map((entry, i) => ({ name: entry.sheet ?? `Sheet${i + 1}`, rows: entry.data }))
  }

  // No signature to sniff — a CSV is plain text. Decided on the name, the one place that is
  // acceptable, because there is nothing in the bytes to decide on (2026-09-17).
  if (kind === 'unknown' && /\.(csv|txt)$/i.test(file.name)) {
    const { csvSheet } = await import('./docxCsv')
    return [csvSheet(new TextDecoder('utf-8').decode(bytes), file.name)]
  }
  return null
}

/**
 * The file's item lines, or a result explaining why there are none.
 *
 * Returns `ReadItemsResult` — the same shape the mail path produces — so the caller renders one
 * summary, `describeRead` writes one note, and a file that cannot be read reports the same way an
 * unreadable attachment does.
 */
export async function readUploadedFile(file: File): Promise<ReadItemsResult> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const kind = sniffFileKind(bytes.subarray(0, HEAD))

  if (kind === 'pdf') {
    let text = ''
    try {
      const { extractText, getDocumentProxy } = await import('unpdf')
      const pdf = await getDocumentProxy(bytes)
      const extracted = await extractText(pdf, { mergePages: true })
      text = Array.isArray(extracted.text) ? extracted.text.join('\n') : String(extracted.text ?? '')
    } catch {
      text = ''
    }
    return readMailItems('', [{ name: file.name, kind: 'pdf', text }])
  }

  try {
    const sheets = await sheetsFrom(file, bytes)
    if (sheets && sheets.length > 0) {
      return readMailItems('', [{ name: file.name, kind: 'xlsx', sheets }])
    }
  } catch {
    // Falls through to the generic report below. A parser that threw on this file tells the
    // purchaser nothing they can act on; "we could not read it" does.
  }

  // Reported through the same path as an unreadable attachment, so the wording, the AI fallback
  // and the "not read" note are all the ones they already know.
  return readMailItems('', [{ name: file.name, kind: 'file' }])
}
